import React from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { designSystem, Modal, Panel, PanelGroup } from '@yola/ws-ui';
import Breadcrumbs from 'src/js/modules/common/components/breadcrumbs';
import { surfacesKeyMap, ORDERED_COLORS } from '../constants/colors-maps';
import constants from '../constants';

const {
  Stack,
  ColorTile,
  ColorPicker,
  Box,
  HorizontalStack,
  DialogHeader,
  DialogButton,
  DialogButtonGroup,
  DialogButtonGroupSeparator,
  Heading,
  Paragraph,
  ColorPalette,
  Divider,
  InteractiveBox,
} = designSystem;

const { DEFAULT_OFFSET_Y, DEFAULT_OFFSET_X } = constants;

const FLOATING_ELEMENT_SELECTOR = '.ws-ui-base-select-option, .ws-ui-base-select__option';

const BaseColorsPickerDialog = ({
  colors,
  selectedColor,
  presets,
  captions,
  displayWarning,
  offsetX = DEFAULT_OFFSET_X,
  offsetY = DEFAULT_OFFSET_Y,
  onSubmit,
  onCancel,
  onDragEnd,
  onActiveColorSelect,
  onColorChange,
  onColorChangeComplete,
  onPresetSelect,
}) => (
  <Modal
    draggable
    resizable={false}
    fullscreen={bowser.mobile}
    width={360}
    height={800}
    offsetX={offsetX}
    offsetY={offsetY}
    dragHandleSelector=".ws-drag-trigger"
    dragCancelSelector=".ws-ui-dialog-header-controls"
    overlay="none"
    handleCancel={onCancel}
    handleSubmit={onSubmit}
    onDragEnd={onDragEnd}
    onOverlayClick={onSubmit}
    floatingModalElementSelector={FLOATING_ELEMENT_SELECTOR}
  >
    <PanelGroup height="100%">
      <Panel
        align="middle"
        corners="squared"
        className="ws-drag-trigger"
        style={{ flexShrink: '0' }}
      >
        <DialogHeader paddingBottom="spacing-s">
          <Box>
            <Box paddingBottom="spacing-2-xs">
              <Breadcrumbs breadcrumbs={captions.breadcrumbs} />
            </Box>
            <Box>
              <Stack>
                <Heading type="heading-3">{captions.title}</Heading>
                <Paragraph>{captions.description}</Paragraph>
              </Stack>
            </Box>
          </Box>
        </DialogHeader>
      </Panel>
      <div className="ws-base-colors-picker__content">
        <Box
          paddingTop="spacing-m"
          paddingBottom="spacing-m"
          paddingLeft="spacing-m"
          paddingRight="spacing-m"
        >
          <HorizontalStack gap="spacing-2-xs" align="space-between">
            {Object.entries(surfacesKeyMap).map(([colorType, colorKey]) => {
              const tooltipText = captions.tooltips[colorType];

              return (
                <ColorTile
                  size="extra-extra-large"
                  key={colorType}
                  active={selectedColor === colorKey}
                  empty
                  backgroundColor={colors[colorKey]}
                  onClick={() => onActiveColorSelect(colorKey)}
                  {...(tooltipText && { tooltipText, tooltipWidth: 250 })}
                />
              );
            })}
          </HorizontalStack>
          <Box marginTop="spacing-xs">
            <ColorPicker
              color={colors[selectedColor]}
              onChange={onColorChange}
              onChangeComplete={onColorChangeComplete}
              preventInputOverflow
              {...(displayWarning && { notificationTitle: captions.warning })}
            />
          </Box>
        </Box>
        <Divider />
        <Box
          paddingTop="spacing-s"
          paddingBottom="spacing-m"
          paddingLeft="spacing-m"
          paddingRight="spacing-m"
        >
          <Stack gap="spacing-3-xs">
            <Heading type="heading-6">{captions.presetsSubtitle}</Heading>
            <Stack gap="spacing-xs">
              {presets.map((preset) => (
                <InteractiveBox
                  key={preset.id}
                  borderRadius="small"
                  borderless
                  focusable
                  onClick={() => onPresetSelect(preset)}
                >
                  <ColorPalette colors={ORDERED_COLORS.map((key) => preset.colors[key])} />
                </InteractiveBox>
              ))}
            </Stack>
          </Stack>
        </Box>
      </div>
      <Panel corners="squared" style={{ flexShrink: '0', borderTop: 0 }}>
        <DialogButtonGroup>
          <DialogButton onClick={onSubmit}>{captions.submitButton}</DialogButton>
          <DialogButtonGroupSeparator />
          <DialogButton onClick={onCancel}>{captions.cancelButton}</DialogButton>
        </DialogButtonGroup>
      </Panel>
    </PanelGroup>
  </Modal>
);

BaseColorsPickerDialog.propTypes = {
  colors: PropTypes.object.isRequired,
  selectedColor: PropTypes.string.isRequired,
  presets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      colors: PropTypes.object.isRequired,
    })
  ).isRequired,
  captions: PropTypes.shape({
    submitButton: PropTypes.string.isRequired,
    cancelButton: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.arrayOf(PropTypes.string).isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    warning: PropTypes.string.isRequired,
    tooltips: PropTypes.shape({
      primary: PropTypes.string,
      secondary: PropTypes.string,
    }).isRequired,
    presetsSubtitle: PropTypes.string.isRequired,
  }).isRequired,
  displayWarning: PropTypes.bool.isRequired,
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onActiveColorSelect: PropTypes.func.isRequired,
  onColorChange: PropTypes.func.isRequired,
  onColorChangeComplete: PropTypes.func.isRequired,
  onPresetSelect: PropTypes.func.isRequired,
};

export default BaseColorsPickerDialog;
